import { useTranslation } from 'react-i18next';
import { AgentTools, type ToolCall, type ToolCallSummary } from '@/features/types/tools';
import { ToolCallContainer } from '../../ToolCallContainer';
import { ToolCallDataList } from '../../ToolCallDataList';
import { useToolCallParser, ToolCallState } from '../../useToolCallParser';
import { ToolCallError } from '../../ToolCallError';

interface UpdateRecordTool {
  name: AgentTools.sf_update_record;
  args: {
    objectName: string;
    recordId: string;
    data: Record<string, unknown>;
    allOrNone?: boolean;
    retries?: number;
    retryDelay?: number;
  };
}

export type UpdateRecordToolCall = ToolCall<UpdateRecordTool['name'], UpdateRecordTool['args']>;

export type UpdateRecordToolCallSummary = ToolCallSummary<
  UpdateRecordTool['name'],
  UpdateRecordTool['args']
>;

interface UpdateRecordResult {
  id: string;
  success: boolean;
  errors: string[];
}

export function UpdateRecordToolCall({ toolCall }: { toolCall: UpdateRecordToolCallSummary }) {
  const { t } = useTranslation('prince');

  const { parsedResult, title } = useToolCallParser<UpdateRecordResult>(
    toolCall,
    ({ toolCallState }) => {
      switch (toolCallState) {
        case ToolCallState.Running:
          return t('toolCalls.sf_update_record.title.whileRunning');
        case ToolCallState.Error:
          return t('toolCalls.sf_update_record.title.errors');
        case ToolCallState.Success:
          return t('toolCalls.sf_update_record.title.success');
      }
    }
  );

  const tabs = [
    {
      id: 'results',
      label: t('toolCalls.results'),
      showLoadingWhileRunning: true,
      content: parsedResult ? (
        parsedResult.hasErrors ? (
          <ToolCallError parsedResult={parsedResult} />
        ) : (
          <ToolCallDataList
            items={[
              {
                label: t('toolCalls.sf_update_record.results.updatedRecordId'),
                value: parsedResult.results.id,
              },
            ]}
          />
        )
      ) : null,
    },
    {
      id: 'parameters',
      label: t('toolCalls.parameters'),
      content: (
        <ToolCallDataList
          items={[
            {
              label: t('toolCalls.sf_update_record.parameters.objectName'),
              value: toolCall.args.objectName,
            },
            {
              label: t('toolCalls.sf_update_record.parameters.recordId'),
              value: toolCall.args.recordId,
            },
            ...Object.entries(toolCall.args.data || {}).map(([key, value]) => ({
              label: key,
              value: value?.toString() || '',
            })),
          ]}
        />
      ),
    },
  ];

  return (
    <ToolCallContainer
      toolName={toolCall.name}
      title={title}
      isRunning={toolCall.isRunning}
      tabs={tabs}
    />
  );
}
